import React, { useEffect, useState } from 'react';
import * as api from '../api';

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = React.useCallback(() => {
    Promise.all([
      api.getAnalytics({ days }),
      api.getApiUsage({ hours: '24' }),
    ])
      .then(([analytics, usage]) => {
        setData(analytics);
        setApiData(usage);
        setLastUpdated(new Date());
      })
      .catch((error) => {
        console.error('Analytics fetch failed:', error);
      })
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    // Refresh every 30 seconds for live tracking
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) return <div className="loading">Loading analytics...</div>;
  if (!data) return <div className="empty-state"><p>Unable to load analytics.</p></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Analytics</h1>
        <p>Usage statistics and scan analytics</p>
      </div>

      <div className="toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <select className="filter-select" value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <button className="btn btn-secondary" onClick={() => { setLoading(true); fetchData(); }} style={{ padding: '6px 14px' }}>
          ↻ Refresh
        </button>
        {lastUpdated && (
          <span style={{ fontSize: 12, color: '#888' }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Scans</div>
          <div className="stat-value">{data.totalScans}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Response Time</div>
          <div className="stat-value">{data.avgResponseTimeMs}<span style={{ fontSize: 14, color: '#888' }}>ms</span></div>
        </div>
        {apiData && (
          <>
            <div className="stat-card">
              <div className="stat-label">API Requests (24h)</div>
              <div className="stat-value">{apiData.totalRequests}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Error Rate (24h)</div>
              <div className="stat-value">{apiData.errorRate.percentage}<span style={{ fontSize: 14, color: '#888' }}>%</span></div>
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Scans per day */}
        <div className="card">
          <div className="card-header"><span className="card-title">Scans Per Day</span></div>
          {data.scansPerDay.length === 0 ? (
            <p style={{ color: '#888', fontSize: 14 }}>No scan data yet.</p>
          ) : (
            <table>
              <thead><tr><th>Date</th><th>Scans</th></tr></thead>
              <tbody>
                {data.scansPerDay.slice(0, 14).map((r) => (
                  <tr key={r.date}>
                    <td>{r.date}</td>
                    <td>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top produce */}
        <div className="card">
          <div className="card-header"><span className="card-title">Most Scanned Produce</span></div>
          {data.topProduce.length === 0 ? (
            <p style={{ color: '#888', fontSize: 14 }}>No produce data yet.</p>
          ) : (
            <table>
              <thead><tr><th>Produce</th><th>Scans</th></tr></thead>
              <tbody>
                {data.topProduce.map((r) => (
                  <tr key={r.produce_detected}>
                    <td style={{ textTransform: 'capitalize' }}>{r.produce_detected}</td>
                    <td>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Mode split */}
        <div className="card">
          <div className="card-header"><span className="card-title">Analysis Mode Split</span></div>
          {data.modeSplit.length === 0 ? (
            <p style={{ color: '#888', fontSize: 14 }}>No data.</p>
          ) : (
            <table>
              <thead><tr><th>Mode</th><th>Count</th></tr></thead>
              <tbody>
                {data.modeSplit.map((r) => (
                  <tr key={r.analysis_mode}>
                    <td><span className={`badge badge-${r.analysis_mode === 'online' ? 'green' : 'yellow'}`}>{r.analysis_mode}</span></td>
                    <td>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Peak hours */}
        <div className="card">
          <div className="card-header"><span className="card-title">Peak Hours (Last 7 Days)</span></div>
          {data.peakHours.length === 0 ? (
            <p style={{ color: '#888', fontSize: 14 }}>No data.</p>
          ) : (
            <table>
              <thead><tr><th>Hour</th><th>Scans</th></tr></thead>
              <tbody>
                {data.peakHours.map((r) => (
                  <tr key={r.hour}>
                    <td>{String(r.hour).padStart(2, '0')}:00</td>
                    <td>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Slow endpoints */}
      {apiData && apiData.slowEndpoints.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><span className="card-title">Slowest Endpoints (24h)</span></div>
          <table>
            <thead><tr><th>Endpoint</th><th>Method</th><th>Avg (ms)</th><th>Requests</th></tr></thead>
            <tbody>
              {apiData.slowEndpoints.map((e, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{e.endpoint}</td>
                  <td><span className="badge badge-gray">{e.method}</span></td>
                  <td>{e.avgMs}</td>
                  <td>{e.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
